import { useMemo, useState } from "react";

import { WorkerAvatar } from "../../../../../UI";
import { Chevron, CheckIcon, SearchSmall } from "../icons";

import { TeamRailStyles } from "./teamRail.styles";

// Computes utilization% for a worker based on the sum of active jobs'
// durations vs. the workday window. Frontend-only.
const computeUtilization = (worker, windowH) => {
  const buckets = [
    worker.inProgressServices,
    worker.acceptedServices,
    worker.pausedServices
  ];
  let minutes = 0;
  for (const list of buckets) {
    if (!Array.isArray(list)) continue;
    for (const s of list) {
      // Shop services occupy a fixed bay, not a worker's time window.
      if (s.mobility === "shop") continue;
      if (typeof s.timeRaw === "number" && typeof s.etaRaw === "number") {
        minutes += Math.max(0, (s.etaRaw - s.timeRaw) / 60);
      }
    }
  }
  if (windowH <= 0) return 0;
  return Math.min(100, Math.round((minutes / (windowH * 60)) * 100));
};

const TeamRail = ({
  workers,
  selectedIds,
  onToggleWorker,
  onSelectAll,
  onClearAll,
  collapsed,
  onCollapse,
  onExpand,
  workdayHours
}) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query) return workers;
    const q = query.toLowerCase();
    return workers.filter(w =>
      `${w.firstName || ""} ${w.lastName || ""} ${w.role?.name || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [workers, query]);

  if (collapsed) {
    return (
      <TeamRailStyles>
        <div className="collapsed-strip" onClick={onExpand}>
          <button type="button" className="collapse-icon" aria-label="Expand team">
            <Chevron dir="r" size={12} />
          </button>
          <div className="collapsed-label">
            Team · {selectedIds.length}/{workers.length}
          </div>
        </div>
      </TeamRailStyles>
    );
  }

  const allSelected = workers.length > 0 && selectedIds.length === workers.length;

  return (
    <TeamRailStyles>
      <div className="header">
        <div className="header-row">
          <div className="title">
            Team
            <span className="count">
              {selectedIds.length}/{workers.length}
            </span>
          </div>
          <div className="actions">
            <button
              type="button"
              className="all-btn"
              onClick={() => (allSelected ? onClearAll() : onSelectAll())}
            >
              {allSelected ? "Clear" : "All"}
            </button>
            <button
              type="button"
              className="collapse-btn"
              onClick={onCollapse}
              aria-label="Collapse team rail"
            >
              <Chevron dir="l" size={12} />
            </button>
          </div>
        </div>
        <div className="search-box">
          <SearchSmall size={12} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search workers"
          />
        </div>
      </div>

      <div className="list">
        {filtered.length === 0 ? (
          <div className="empty">No workers</div>
        ) : (
          filtered.map(w => {
            const checked = selectedIds.includes(w.id);
            const u = computeUtilization(w, workdayHours);
            const fillClass = u > 85 ? "high" : u > 60 ? "" : "low";
            return (
              <div
                key={w.id}
                className={`row ${checked ? "checked" : ""}`}
                onClick={() => onToggleWorker(w.id)}
              >
                <div className={`checkbox ${checked ? "checked" : ""}`}>
                  {checked && <CheckIcon size={9} />}
                </div>
                <WorkerAvatar
                  firstName={w.firstName}
                  lastName={w.lastName}
                  profileImage={w.profileImage}
                  size={28}
                />
                <div className="meta">
                  <div className="name">
                    {w.firstName} {w.lastName}
                  </div>
                  {w.role?.name && <div className="sub">{w.role.name}</div>}
                </div>
                <div className="util">
                  <div className="pct">{u}%</div>
                  <div className="bar">
                    <div className={`fill ${fillClass}`} style={{ width: `${u}%` }} />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </TeamRailStyles>
  );
};

export default TeamRail;
